/**
 * The page frame: one container for every page, so titles start at the same
 * x on every page and large screens are used.
 *
 * Pages pick a layout in their route meta (`definePage({ meta: { layout } })`):
 * - "default": centred, up to 76rem (about 1120px of content, the width every
 *   page was designed for), gutters 16 / 24 / 32 / 48px
 * - "wide": same gutters, up to 110rem (1760px): dashboards and card grids
 *   that are built to use a large screen
 * - "full": same gutters, no cap (audit, graph views, wide tables)
 * - "narrow": the default frame, content kept to a reading width at its left
 *   edge (forms, prose)
 * `wide: true` (older pages) means "full".
 */

export type PageLayout = "default" | "wide" | "full" | "narrow";

declare module "vue-router" {
  interface RouteMeta {
    /** Page frame width (components/shell/layout.ts). */
    layout?: PageLayout;
  }
}

/** Horizontal gutters, shared with the top bar so its search lines up with page titles. */
export const GUTTER = "px-4 sm:px-6 lg:px-8 2xl:px-12";

export function pageLayout(meta: { layout?: PageLayout; wide?: boolean }): PageLayout {
  if (meta.layout) return meta.layout;
  return meta.wide ? "full" : "default";
}

const CAP: Record<PageLayout, string> = {
  default: "max-w-[76rem]",
  narrow: "max-w-[76rem]",
  wide: "max-w-[110rem]",
  full: "",
};

/** Classes for the page container. */
export function frameClass(layout: PageLayout): string {
  return `mx-auto w-full ${GUTTER} pt-6 pb-10 lg:pt-8 lg:pb-12 ${CAP[layout]}`.trimEnd();
}

/** Inner width for the narrow layout (left-aligned, not re-centred). */
export const NARROW = "max-w-[52rem]";
